import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    message,
    Card,
    Popconfirm,
    Tooltip,
    InputNumber,
    Tabs,
    Typography,
    Row,
    Col,
    Statistic
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    LinkOutlined,
    DisconnectOutlined,
    CloudOutlined,
    SyncOutlined,
    GlobalOutlined
} from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';

const { Option } = Select;
const { Text } = Typography;

const EipManagement = () => {
    const [eips, setEips] = useState([]);
    const [pools, setPools] = useState([]);
    const [vms, setVms] = useState([]);
    const [nodes, setNodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [allocateModalVisible, setAllocateModalVisible] = useState(false);
    const [bindModalVisible, setBindModalVisible] = useState(false);
    const [blockModalVisible, setBlockModalVisible] = useState(false);
    const [currentEip, setCurrentEip] = useState(null);
    const [allocateType, setAllocateType] = useState('v4');
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [stats, setStats] = useState({ total: 0, allocated: 0, bound: 0, available: 0 });

    const [allocateForm] = Form.useForm();
    const [bindForm] = Form.useForm();
    const [blockForm] = Form.useForm();

    useEffect(() => {
        loadEips();
        loadPools();
        loadVms();
        loadNodes();
    }, []);

    // Helper to extract array from paginated response
    const extractArray = (data, ...keys) => {
        if (Array.isArray(data)) return data;
        for (const key of keys) {
            if (Array.isArray(data?.[key])) return data[key];
        }
        if (Array.isArray(data?.data)) return data.data;
        return [];
    };

    const loadEips = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterType !== 'all') params.type = filterType;
            if (filterStatus !== 'all') params.status = filterStatus;

            const response = await evmAdminService.getEipList(params);
            if (response.status === 'success') {
                const eipList = extractArray(response.data, 'eips');
                setEips(eipList);
                // Calculate stats
                const newStats = {
                    total: eipList.length,
                    allocated: eipList.filter(e => e.status === 'allocated').length,
                    bound: eipList.filter(e => e.status === 'bound').length,
                    available: eipList.filter(e => e.status === 'available').length
                };
                setStats(newStats);
            } else {
                message.error(response.msg || 'Failed to load EIPs');
            }
        } catch (error) {
            message.error('Failed to load EIPs');
        } finally {
            setLoading(false);
        }
    };

    const loadPools = async () => {
        try {
            const response = await evmAdminService.getIpPoolList();
            if (response.status === 'success') {
                setPools(extractArray(response.data, 'pools'));
            }
        } catch (error) {
            console.error('Failed to load pools', error);
        }
    };

    const loadVms = async () => {
        try {
            const response = await evmAdminService.getVms();
            if (response.status === 'success') {
                setVms(extractArray(response.data, 'vms'));
            }
        } catch (error) {
            console.error('Failed to load VMs', error);
        }
    };

    const loadNodes = async () => {
        try {
            const response = await evmAdminService.getNodeList();
            if (response.status === 'success') {
                setNodes(extractArray(response.data, 'nodes'));
            }
        } catch (error) {
            console.error('Failed to load nodes', error);
        }
    };

    const showAllocateModal = (type) => {
        setAllocateType(type);
        allocateForm.resetFields();
        allocateForm.setFieldsValue({ mode: 'single' });
        setAllocateModalVisible(true);
    };

    const showBindModal = (eip) => {
        setCurrentEip(eip);
        bindForm.resetFields();
        setBindModalVisible(true);
    };

    const handleAllocate = async (values) => {
        try {
            let response;
            if (values.mode === 'batch') {
                if (allocateType === 'v4') {
                    response = await evmAdminService.batchAllocateEipV4({
                        pool_id: values.pool_id,
                        user_id: values.user_id,
                        count: values.count
                    });
                } else {
                    response = await evmAdminService.batchAllocateEipV6({
                        pool_id: values.pool_id,
                        user_id: values.user_id,
                        count: values.count
                    });
                }
            } else {
                if (allocateType === 'v4') {
                    response = await evmAdminService.allocateEipV4({
                        pool_id: values.pool_id,
                        user_id: values.user_id,
                        address: values.address
                    });
                } else {
                    response = await evmAdminService.allocateEipV6({
                        pool_id: values.pool_id,
                        user_id: values.user_id,
                        address: values.address
                    });
                }
            }
            if (response.status === 'success') {
                message.success(`Allocated ${response.data?.count || 1} EIP(s)`);
                setAllocateModalVisible(false);
                loadEips();
            } else {
                message.error(response.msg || 'Failed to allocate EIP');
            }
        } catch (error) {
            message.error('Failed to allocate EIP');
        }
    };

    const handleBind = async (values) => {
        try {
            const isV6 = currentEip.ip_version === 'v6';
            const payload = { eip_id: currentEip.id, vm_id: values.vm_id };
            const response = isV6
                ? await evmAdminService.bindEipV6(payload)
                : await evmAdminService.bindEip(payload);
            if (response.status === 'success') {
                message.success('EIP bound to VM');
                setBindModalVisible(false);
                loadEips();
            } else {
                message.error(response.msg || 'Failed to bind EIP');
            }
        } catch (error) {
            message.error('Failed to bind EIP');
        }
    };

    const showBlockModal = () => {
        blockForm.resetFields();
        blockForm.setFieldsValue({ prefix_len: 64 });
        setBlockModalVisible(true);
    };

    const handleAllocateBlock = async (values) => {
        try {
            const response = await evmAdminService.allocateEipV6Block({
                user_id: values.user_id,
                prefix: values.prefix,
                prefix_len: values.prefix_len,
                node_id: values.node_id
            });
            if (response.status === 'success') {
                message.success('IPv6 block allocated');
                setBlockModalVisible(false);
                loadEips();
            } else {
                message.error(response.msg || 'Failed to allocate IPv6 block');
            }
        } catch (error) {
            message.error('Failed to allocate IPv6 block');
        }
    };

    const handleUnbind = async (eip) => {
        try {
            const response = await evmAdminService.unbindEip(eip.id);
            if (response.status === 'success') {
                message.success('EIP unbound');
                loadEips();
            } else {
                message.error(response.msg || 'Failed to unbind EIP');
            }
        } catch (error) {
            message.error('Failed to unbind EIP');
        }
    };

    const handleRelease = async (eip) => {
        try {
            const response = await evmAdminService.releaseEip(eip.id);
            if (response.status === 'success') {
                message.success('EIP released');
                loadEips();
            } else {
                message.error(response.msg || 'Failed to release EIP');
            }
        } catch (error) {
            message.error('Failed to release EIP');
        }
    };

    const getTypeTag = (type) => {
        return type === 'v6' ? (
            <Tag color="purple">IPv6</Tag>
        ) : (
            <Tag color="blue">IPv4</Tag>
        );
    };

    const getStatusTag = (status) => {
        const statusMap = {
            'available': { color: 'green', text: 'Available' },
            'allocated': { color: 'orange', text: 'Allocated' },
            'bound': { color: 'blue', text: 'Bound' },
            'reserved': { color: 'default', text: 'Reserved' }
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const getNodeName = (nodeId) => {
        const node = nodes.find(n => n.id === nodeId);
        return node ? node.name : `Node ${nodeId}`;
    };

    const getVmName = (vmId) => {
        const vm = vms.find(v => v.id === vmId);
        return vm ? vm.hostname || `VM-${vmId}` : `VM ${vmId}`;
    };

    const getPoolName = (poolId) => {
        const pool = pools.find(p => p.id === poolId);
        return pool ? pool.name : `Pool ${poolId}`;
    };

    const filteredPools = pools.filter(p => p.type === allocateType);

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60
        },
        {
            title: 'Address',
            dataIndex: 'public_ip',
            key: 'public_ip',
            width: 200,
            render: (ip) => <Text copyable>{ip}</Text>
        },
        {
            title: 'Type',
            dataIndex: 'ip_version',
            key: 'ip_version',
            width: 80,
            render: (type) => getTypeTag(type)
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => getStatusTag(status)
        },
        {
            title: 'Node',
            key: 'node',
            width: 120,
            render: (_, record) => record.node?.name || (record.node_id ? getNodeName(record.node_id) : '-')
        },
        {
            title: 'User ID',
            dataIndex: 'user_id',
            key: 'user_id',
            width: 80
        },
        {
            title: 'Bound VM',
            key: 'vm',
            width: 150,
            render: (_, record) => record.vm?.hostname || record.vm?.name || (record.vm_id ? getVmName(record.vm_id) : '-')
        },
        {
            title: 'Actions',
            key: 'action',
            fixed: 'right',
            width: 180,
            render: (_, record) => (
                <Space size="small">
                    {record.status === 'allocated' && (
                        <Tooltip title="Bind to VM">
                            <Button
                                size="small"
                                type="primary"
                                icon={<LinkOutlined />}
                                onClick={() => showBindModal(record)}
                            />
                        </Tooltip>
                    )}
                    {record.status === 'bound' && (
                        <Popconfirm
                            title="Unbind this EIP?"
                            onConfirm={() => handleUnbind(record)}
                        >
                            <Tooltip title="Unbind">
                                <Button size="small" icon={<DisconnectOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    )}
                    {(record.status === 'allocated' || record.status === 'available') && (
                        <Popconfirm
                            title="Release this EIP?"
                            description="The IP will be returned to the pool."
                            onConfirm={() => handleRelease(record)}
                        >
                            <Tooltip title="Release">
                                <Button size="small" danger icon={<DeleteOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div>
            <Card
                title={<span><GlobalOutlined /> 弹性IP管理</span>}
                extra={
                    <Space>
                        <Select
                            value={filterType}
                            onChange={(v) => { setFilterType(v); }}
                            style={{ width: 100 }}
                        >
                            <Option value="all">All Types</Option>
                            <Option value="v4">IPv4</Option>
                            <Option value="v6">IPv6</Option>
                        </Select>
                        <Select
                            value={filterStatus}
                            onChange={(v) => { setFilterStatus(v); }}
                            style={{ width: 120 }}
                        >
                            <Option value="all">All Status</Option>
                            <Option value="available">Available</Option>
                            <Option value="allocated">Allocated</Option>
                            <Option value="bound">Bound</Option>
                        </Select>
                        <Button onClick={loadEips}>Filter</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => showAllocateModal('v4')}>
                            Allocate IPv4
                        </Button>
                        <Button icon={<PlusOutlined />} onClick={() => showAllocateModal('v6')}>
                            Allocate IPv6
                        </Button>
                        <Button icon={<GlobalOutlined />} onClick={showBlockModal}>
                            Allocate IPv6 Block
                        </Button>
                        <Button icon={<SyncOutlined />} onClick={loadEips}>
                            Refresh
                        </Button>
                    </Space>
                }
            >
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={6}>
                        <Statistic title="EIP 总数" value={stats.total} />
                    </Col>
                    <Col span={6}>
                        <Statistic title="可用" value={stats.available} valueStyle={{ color: '#3f8600' }} />
                    </Col>
                    <Col span={6}>
                        <Statistic title="已分配" value={stats.allocated} valueStyle={{ color: '#faad14' }} />
                    </Col>
                    <Col span={6}>
                        <Statistic title="已绑定" value={stats.bound} valueStyle={{ color: '#1890ff' }} />
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={eips}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 1200 }}
                    pagination={{ pageSize: 20 }}
                />
            </Card>

            {/* Allocate EIP Modal */}
            <Modal
                title={`Allocate ${allocateType === 'v6' ? 'IPv6' : 'IPv4'} EIP`}
                open={allocateModalVisible}
                onCancel={() => setAllocateModalVisible(false)}
                onOk={() => allocateForm.submit()}
            >
                <Form form={allocateForm} layout="vertical" onFinish={handleAllocate}>
                    <Form.Item
                        name="pool_id"
                        label="IP Pool"
                        rules={[{ required: true, message: 'Please select IP pool' }]}
                    >
                        <Select placeholder="Select IP pool">
                            {filteredPools.map(pool => (
                                <Option key={pool.id} value={pool.id}>
                                    {pool.name} ({pool.cidr}) - {pool.total_count - pool.used_count} available
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="user_id"
                        label="User ID"
                        rules={[{ required: true, message: 'Please enter user ID' }]}
                    >
                        <InputNumber min={1} style={{ width: '100%' }} placeholder="User ID to allocate to" />
                    </Form.Item>

                    <Form.Item name="mode" label="Allocation Mode" initialValue="single">
                        <Select>
                            <Option value="single">Single IP</Option>
                            <Option value="batch">Batch Allocate</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) => prev.mode !== curr.mode}
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('mode') === 'batch' ? (
                                <Form.Item
                                    name="count"
                                    label="Count"
                                    rules={[{ required: true, message: 'Please enter count' }]}
                                >
                                    <InputNumber min={1} max={100} style={{ width: '100%' }} placeholder="Number of IPs to allocate" />
                                </Form.Item>
                            ) : (
                                <Form.Item
                                    name="address"
                                    label="Specific Address (Optional)"
                                    tooltip="Leave empty to auto-assign"
                                >
                                    <Input placeholder="e.g., 192.168.1.100 (optional)" />
                                </Form.Item>
                            )
                        }
                    </Form.Item>
                </Form>
            </Modal>

            {/* Bind EIP Modal */}
            <Modal
                title={`Bind EIP: ${currentEip?.address || ''}`}
                open={bindModalVisible}
                onCancel={() => setBindModalVisible(false)}
                onOk={() => bindForm.submit()}
            >
                <Form form={bindForm} layout="vertical" onFinish={handleBind}>
                    <Form.Item
                        name="vm_id"
                        label="Select VM"
                        rules={[{ required: true, message: 'Please select a VM' }]}
                    >
                        <Select
                            placeholder="Select VM to bind"
                            showSearch
                            optionFilterProp="children"
                        >
                            {vms.filter(vm => vm.uid === currentEip?.user_id).map(vm => (
                                <Option key={vm.id} value={vm.id}>
                                    {vm.hostname || vm.name || `VM-${vm.id}`}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Allocate IPv6 Block Modal */}
            <Modal
                title="Allocate IPv6 Block"
                open={blockModalVisible}
                onCancel={() => setBlockModalVisible(false)}
                onOk={() => blockForm.submit()}
            >
                <Form form={blockForm} layout="vertical" onFinish={handleAllocateBlock}>
                    <Form.Item
                        name="user_id"
                        label="User ID"
                        rules={[{ required: true, message: 'Please enter user ID' }]}
                    >
                        <InputNumber style={{ width: '100%' }} min={1} placeholder="Target user ID" />
                    </Form.Item>
                    <Form.Item
                        name="prefix"
                        label="IPv6 Prefix"
                        rules={[{ required: true, message: 'Please enter IPv6 prefix' }]}
                        extra="e.g. 2001:db8:1:100::"
                    >
                        <Input placeholder="2001:db8:1:100::" />
                    </Form.Item>
                    <Form.Item
                        name="prefix_len"
                        label="Prefix Length"
                        rules={[{ required: true, message: 'Please enter prefix length' }]}
                        extra="Between 48 and 64"
                    >
                        <InputNumber style={{ width: '100%' }} min={48} max={64} />
                    </Form.Item>
                    <Form.Item name="node_id" label="Node (optional)">
                        <Select placeholder="Select node" allowClear showSearch optionFilterProp="children">
                            {nodes.map(node => (
                                <Option key={node.id} value={node.id}>{node.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default EipManagement;
